import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_ENDPOINT;


const statusOptions = [
  { label: 'Pending', value: 'PENDING' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Rejected', value: 'REJECTED' },
];

const TeamResignationRequests = () => {
  const [resignations, setResignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvingId, setApprovingId] = useState(null);
  const [selectedStatuses, setSelectedStatuses] = useState(['PENDING', 'APPROVED', 'REJECTED', 'EXITED']);
  const [showExitDetails, setShowExitDetails] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchResignations();
  }, []);

  const handleAuthError = () => {
    // Clear tokens
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    // Redirect to login
    navigate('/login');
    toast.error('Your session has expired. Please login again.');
  };

  const refreshToken = async () => {
    try {
      const refresh_token = localStorage.getItem('refresh_token');
      if (!refresh_token) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post(`${API_BASE}/api/token/refresh/`, {
        refresh: refresh_token
      });

      localStorage.setItem('access_token', response.data.access);
      return response.data.access;
    } catch (error) {
      console.error('Error refreshing token:', error);
      handleAuthError();
      throw error;
    }
  };

  const fetchResignations = async () => {
    setLoading(true);
    try {
      let token = localStorage.getItem('access_token');
      
      try {
        const response = await axios.get(`${API_BASE}/api/employee/resignation/requests/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setResignations(response.data);
      } catch (error) {
        if (error.response?.status === 401) {
          // Token expired, try to refresh
          token = await refreshToken();
          // Retry the request with new token
          const response = await axios.get(`${API_BASE}/api/employee/resignation/requests/`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          setResignations(response.data);
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('Error fetching resignations:', error);
      if (error.response?.status === 401) {
        handleAuthError();
      } else {
        toast.error('Failed to fetch resignation requests');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApproveReject = async (id, status) => {
    setApprovingId(id);
    try {
      const response = await axios.put(
        `${API_BASE}/api/employee/resignation/requests/${id}/`,
        { 
          status,
          manager_signature: 'Digital Signature'  // Adding manager signature
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );
      toast.success(`Resignation ${status.toLowerCase()} successfully`);
      fetchResignations();
    } catch (error) {
      console.error('Error updating resignation:', error);
      toast.error(error.response?.data?.error || `Failed to ${status.toLowerCase()} resignation`);
    } finally {
      setApprovingId(null);
    }
  };

  const handleViewExitForm = (resignation) => {
    setSelectedRequest(resignation);
    setShowExitDetails(true);
  };

  const handleStatusChange = (status) => {
    setSelectedStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const filteredResignations = resignations.filter(resignation => 
    selectedStatuses.includes(resignation.status)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center">
          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Resignation Requests</h2>
        <div className="flex flex-wrap gap-4 items-center mb-4">
          {[
            { value: 'PENDING', label: 'Pending' },
            { value: 'APPROVED', label: 'Approved' },
            { value: 'REJECTED', label: 'Rejected' },
            { value: 'EXITED', label: 'Exited' }
          ].map(status => (
            <label key={status.value} className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={selectedStatuses.includes(status.value)}
                onChange={() => handleStatusChange(status.value)}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {status.label}
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Employee
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Department
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Working Day
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Reason
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredResignations.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                  No resignation requests found for the selected statuses.
                </td>
              </tr>
            ) : (
              filteredResignations.map((resignation) => (
                <tr key={resignation.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{resignation.employee_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{resignation.department}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {new Date(resignation.last_working_day).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500 max-w-xs truncate">
                      {resignation.reason_for_leaving}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                      ${resignation.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                        resignation.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                        resignation.status === 'EXITED' ? 'bg-gray-100 text-gray-800' :
                        'bg-yellow-100 text-yellow-800'}`}>
                      {resignation.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {resignation.status === 'PENDING' && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleApproveReject(resignation.id, 'APPROVED')}
                          disabled={approvingId === resignation.id}
                          className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                        >
                          {approvingId === resignation.id ? (
                            <div className="flex items-center">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing...
                            </div>
                          ) : (
                            'Approve'
                          )}
                        </button>
                        <button
                          onClick={() => handleApproveReject(resignation.id, 'REJECTED')}
                          disabled={approvingId === resignation.id}
                          className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                        >
                          {approvingId === resignation.id ? (
                            <div className="flex items-center">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing...
                            </div>
                          ) : (
                            'Reject'
                          )}
                        </button>
                      </div>
                    )}

                    {resignation.status === 'EXITED' && (
                      <button
                        onClick={() => handleViewExitForm(resignation)}
                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        View Exit Form
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showExitDetails && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Exit Form Details</h2>
              <button
                onClick={() => setShowExitDetails(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Employee Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Employee Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Name</p>
                    <p className="text-sm text-gray-900">{selectedRequest.employee_name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Department</p>
                    <p className="text-sm text-gray-900">{selectedRequest.department}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Designation</p>
                    <p className="text-sm text-gray-900">{selectedRequest.designation}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Status</p>
                    <p className="text-sm text-gray-900">{selectedRequest.status}</p>
                  </div>
                </div>
              </div>

              {/* Resignation Details */}
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Resignation Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Last Working Day</p>
                    <p className="text-sm text-gray-900">{new Date(selectedRequest.last_working_day).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Notice Period Served</p>
                    <p className="text-sm text-gray-900">{selectedRequest.served_notice_period} days</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Buy Out</p>
                    <p className="text-sm text-gray-900">{selectedRequest.buy_out ? 'Yes' : 'No'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Reason for Leaving</p>
                    <p className="text-sm text-gray-900">{selectedRequest.reason_for_leaving}</p>
                  </div>
                </div>
              </div>

              {/* Asset Return Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Asset Return</h3>
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    Assets Returned: {selectedRequest.assets_returned ? 'Yes' : 'No'}
                  </p>
                  {selectedRequest.asset_returned_reason && (
                    <p className="text-sm text-gray-600">
                      Reason: {selectedRequest.asset_returned_reason}
                    </p>
                  )}
                </div>
              </div>

              {/* Confidentiality Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Confidentiality</h3>
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    Confidential Documents Returned: {selectedRequest.confidential_documents_returned ? 'Yes' : 'No'}
                  </p>
                  <p className="text-sm text-gray-600">
                    Confidentiality Agreement: {selectedRequest.confidentiality_agreement ? 'Signed' : 'Not Signed'}
                  </p>
                </div>
              </div>

              {/* Declarations */}
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Declarations</h3>
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    Information Accuracy: {selectedRequest.information_accuracy ? 'Confirmed' : 'Not Confirmed'}
                  </p>
                  <p className="text-sm text-gray-600">
                    Legal Consequences: {selectedRequest.legal_consequences ? 'Understood' : 'Not Understood'}
                  </p>
                </div>
              </div>

              {/* Feedback */}
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Feedback</h3>
                <p className="text-sm text-gray-600">{selectedRequest.feedback || 'No feedback provided'}</p>
              </div>

              {/* Signatures */}
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Signatures</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Employee</p>
                    <p className="text-sm text-gray-900">{selectedRequest.employee_signature}</p>
                    <p className="text-xs text-gray-500">
                      Date: {new Date(selectedRequest.employee_signature_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Manager</p>
                    <p className="text-sm text-gray-900">{selectedRequest.manager_signature || 'Not signed'}</p>
                    {selectedRequest.manager_signature_date && (
                      <p className="text-xs text-gray-500">
                        Date: {new Date(selectedRequest.manager_signature_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Timestamps */}
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Timestamps</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Created At</p>
                    <p className="text-sm text-gray-900">
                      {new Date(selectedRequest.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Last Updated</p>
                    <p className="text-sm text-gray-900">
                      {new Date(selectedRequest.updated_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamResignationRequests; 